import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, apiUrl, buildQueryString } from "./client";
import { useAuthStore } from "@/stores/auth";
import type {
  RenderConfig,
  RenderJob,
  RenderListParams,
} from "@/types/render";

export const renderKeys = {
  all: ["renders"] as const,
  list: (params: RenderListParams) => ["renders", "list", params] as const,
  detail: (id: string) => ["renders", id] as const,
};

export function useRendersQuery(params: RenderListParams = {}) {
  return useQuery({
    queryKey: renderKeys.list(params),
    queryFn: () =>
      apiFetch<RenderJob[]>(
        `/renders${buildQueryString(params as Record<string, string | number | boolean | undefined>)}`,
      ),
  });
}

export function useRenderByIdQuery(id: string, poll = false) {
  return useQuery({
    queryKey: renderKeys.detail(id),
    queryFn: () => apiFetch<RenderJob>(`/renders/${id}`),
    enabled: !!id,
    refetchInterval: poll ? 1000 : false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useCreateRenderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RenderConfig) =>
      apiFetch<RenderJob>("/renders", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: renderKeys.all });
    },
  });
}

export function useDeleteRenderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/renders/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: renderKeys.all });
    },
  });
}

export function getRenderDownloadUrl(id: string, matte = false): string {
  return apiUrl(`/renders/${id}/download${matte ? "?matte=true" : ""}`);
}

/**
 * Download a completed render with auth headers, then trigger a browser save.
 * Mirrors the template-export download pattern. matte=true fetches the
 * companion luma matte of an mp4-matte render.
 */
export async function downloadRender(
  id: string,
  filename?: string,
  matte = false,
): Promise<void> {
  const { token } = useAuthStore.getState();
  const res = await fetch(getRenderDownloadUrl(id, matte), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? `render-${id}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
